import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import {
  ArrowUp,
  SquarePen,
  MessageSquare,
  Trash2,
} from 'lucide-react';
import RuixenMoonChat from '@/components/ui/ruixen-moon-chat';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: number;
}

interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'backr-chat-sessions-v1';
const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? '';
};
const createId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const readChats = (): ChatSession[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatSession[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

async function callAI(message: string): Promise<string> {
  const res = await fetch('/api/chat/ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
    body: JSON.stringify({ message }),
  });
  if (res.status === 401) return 'Your session expired. Please sign in again.';
  if (res.status === 403) return "I'm here to help with startup fundraising and investor discovery on RaiseBeacon. What can I help you with today?";
  if (!res.ok) throw new Error('AI request failed');
  const data = await res.json();
  return data.reply as string;
}

async function callTitle(message: string): Promise<string> {
  try {
    const res = await fetch('/api/chat/title', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
      body: JSON.stringify({ message }),
    });
    if (!res.ok) return message.slice(0, 52);
    const data = await res.json();
    return data.title || message.slice(0, 52);
  } catch {
    return message.slice(0, 52);
  }
}

// ─── Flow Page ────────────────────────────────────────────────────────────────

export function FlowPage() {
  const [chats, setChats] = useState<ChatSession[]>(() => readChats());
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null);
  const [composer, setComposer] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(chats));
  }, [chats]);

  const sortedChats = useMemo(() => [...chats].sort((a, b) => b.updatedAt - a.updatedAt), [chats]);
  const activeChat  = useMemo(() => chats.find((c) => c.id === activeChatId) ?? null, [chats, activeChatId]);
  const isLoading   = loadingChatId === activeChatId;

  useEffect(() => {
    const messages = activeChat?.messages;
    if (!messages?.length) return;
    if (messages[messages.length - 1].role === 'user') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeChat?.messages.length]);

  const startNewChat = () => {
    setActiveChatId(null);
    setComposer('');
    setShowHistory(false);
    setTimeout(() => textareaRef.current?.focus(), 80);
  };

  const sendMessage = (chatId: string, text: string) => {
    const userMsg: ChatMessage = { id: createId(), role: 'user', content: text, createdAt: Date.now() };
    setChats((prev) =>
      prev.map((c) => c.id !== chatId ? c : { ...c, updatedAt: Date.now(), messages: [...c.messages, userMsg] })
    );
    setLoadingChatId(chatId);

    callAI(text)
      .then((reply) => {
        setChats((prev) =>
          prev.map((c) => c.id !== chatId ? c : {
            ...c,
            updatedAt: Date.now(),
            messages: [...c.messages, { id: createId(), role: 'assistant', content: reply, createdAt: Date.now() }],
          })
        );
      })
      .catch(() => {
        setChats((prev) =>
          prev.map((c) => c.id !== chatId ? c : {
            ...c,
            messages: [...c.messages, { id: createId(), role: 'assistant', content: 'Something went wrong. Please try again.', createdAt: Date.now() }],
          })
        );
      })
      .finally(() => setLoadingChatId(null));
  };

  const submitFlowMessage = (raw: string) => {
    const text = raw.trim();
    if (!text) return;

    if (activeChat) {
      sendMessage(activeChat.id, text);
      return;
    }

    const chatId = createId();
    const now = Date.now();
    const newChat: ChatSession = {
      id: chatId,
      title: text.slice(0, 52),
      createdAt: now,
      updatedAt: now,
      messages: [],
    };
    setChats((prev) => [newChat, ...prev]);
    setActiveChatId(chatId);
    setLoadingChatId(chatId);

    callTitle(text).then((title) =>
      setChats((prev) => prev.map((c) => (c.id !== chatId ? c : { ...c, title })))
    );

    const userMsg: ChatMessage = { id: createId(), role: 'user', content: text, createdAt: now };
    setChats((prev) => prev.map((c) => (c.id !== chatId ? c : { ...c, messages: [userMsg] })));

    callAI(text)
      .then((reply) => {
        setChats((prev) =>
          prev.map((c) =>
            c.id !== chatId
              ? c
              : {
                  ...c,
                  updatedAt: Date.now(),
                  messages: [
                    ...c.messages,
                    { id: createId(), role: 'assistant', content: reply, createdAt: Date.now() },
                  ],
                }
          )
        );
      })
      .catch(() => {
        setChats((prev) =>
          prev.map((c) =>
            c.id !== chatId
              ? c
              : {
                  ...c,
                  messages: [
                    ...c.messages,
                    {
                      id: createId(),
                      role: 'assistant',
                      content: 'Something went wrong. Please try again.',
                      createdAt: Date.now(),
                    },
                  ],
                }
          )
        );
      })
      .finally(() => setLoadingChatId(null));
  };

  const handleSend = () => {
    const text = composer.trim();
    if (!text) return;
    setComposer('');
    submitFlowMessage(text);
  };

  const deleteChat = (chatId: string) => {
    setChats((prev) => prev.filter((c) => c.id !== chatId));
    if (activeChatId === chatId) setActiveChatId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
      {/* Header */}
      <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-border/70 px-5">
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-semibold text-foreground">Flow by RaiseBeacon</span>
          {sortedChats.length > 0 && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              className={cn(
                'ml-1 flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                showHistory
                  ? 'bg-[#2B7FFF]/10 text-[#2B7FFF]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <MessageSquare className="h-3 w-3" />
              {sortedChats.length} conversation{sortedChats.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>
        <button
          onClick={startNewChat}
          title="New conversation"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <SquarePen className="h-4 w-4" />
        </button>
      </div>

      {/* Session history */}
      <AnimatePresence>
        {showHistory && sortedChats.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="flex-shrink-0 overflow-hidden border-b border-border/70"
          >
            <ScrollArea className="max-h-52">
              <div className="p-3 space-y-0.5">
                {sortedChats.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => { setActiveChatId(chat.id); setShowHistory(false); }}
                    className={cn(
                      'group flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors',
                      activeChatId === chat.id
                        ? 'bg-[#2B7FFF]/10 text-[#2B7FFF]'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate text-xs">{chat.title}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteChat(chat.id); }}
                      className="ml-3 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages / moon landing */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
        {!activeChat ? (
          <RuixenMoonChat
            onSubmit={submitFlowMessage}
            className="min-h-[min(72vh,640px)] w-full shrink-0"
            heroTitle="Flow"
            heroSubtitle="Search investors, craft outreach, and navigate your raise — start below."
          />
        ) : (
          <div className="mx-auto max-w-3xl space-y-5">
            <AnimatePresence initial={false}>
              {activeChat.messages.map((msg) => {
                const isUser = msg.role === 'user';
                return (
                  <motion.div
                    key={msg.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className={isUser ? 'flex justify-end' : 'flex justify-start'}
                  >
                    <div
                      className={isUser
                        ? 'max-w-[70%] rounded-2xl rounded-tr-sm bg-[#2B7FFF] px-4 py-3 text-sm leading-relaxed text-white'
                        : 'min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-border/60 bg-muted/40 px-5 py-3.5 text-sm leading-relaxed'
                      }
                    >
                      {isUser ? (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      ) : (
                        <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1.5 prose-p:leading-relaxed prose-headings:font-semibold prose-a:text-[#2B7FFF] prose-code:text-[#2B7FFF] prose-code:bg-[#EEF4FF] prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs dark:prose-code:bg-[#2B7FFF]/10">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {isLoading && (
              <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm border border-border/60 bg-muted/40 px-5 py-3.5">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{ animationDelay: `${i * 0.18}s` }}
                      className="typing-dot h-[5px] w-[5px] rounded-full bg-muted-foreground/40"
                    />
                  ))}
                </div>
              </motion.div>
            )}

            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Composer — hidden on moon landing; RuixenMoonChat handles first message */}
      {activeChat && (
        <div className="flex-shrink-0 border-t border-border/70 px-6 py-4">
          <div className="mx-auto max-w-3xl">
            <div className="rounded-2xl border border-border/70 bg-background shadow-sm transition-shadow focus-within:border-[#2B7FFF]/40 focus-within:shadow-[0_0_0_3px_rgba(43,127,255,0.08)]">
              <textarea
                ref={textareaRef}
                rows={2}
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Reply…"
                className="w-full resize-none bg-transparent px-5 pb-1 pt-4 text-sm leading-relaxed placeholder:text-muted-foreground/60 focus:outline-none"
              />
              <div className="flex items-center justify-between px-4 pb-3 pt-1">
                <span className="text-xs text-muted-foreground/50">⏎ send · ⇧⏎ new line</span>
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!composer.trim() || isLoading}
                  className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#2B7FFF] text-white shadow-[0_2px_8px_rgba(43,127,255,0.4)] transition-all hover:bg-[#1A6AE8] active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 disabled:shadow-none"
                >
                  <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlowPage;
